import React, { useEffect, useMemo, useState } from "react";
import {
  Mail, Send, Trash2, Edit2, Loader2, Sparkles, X, Inbox, CheckCircle2,
  AlertCircle, Plus, Search,
} from "lucide-react";
import { toast } from "sonner";

import ConsoleShell from "@/components/ConsoleShell";
import api from "@/lib/api";

export default function Emails() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState("draft"); // draft | sent
  const [q, setQ] = useState("");
  const [showCompose, setShowCompose] = useState(false);
  const [editing, setEditing] = useState(null);

  const refresh = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/emails");
      setItems(data);
    } catch {
      toast.error("Erreur de chargement");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { refresh(); }, []);

  const counts = useMemo(() => ({
    draft: items.filter((e) => e.status === "draft").length,
    sent: items.filter((e) => e.status === "sent").length,
    failed: items.filter((e) => e.status === "failed").length,
  }), [items]);

  const filtered = useMemo(() => {
    let f = items;
    if (tab === "draft") f = f.filter((e) => e.status === "draft" || e.status === "failed");
    else if (tab === "sent") f = f.filter((e) => e.status === "sent");
    if (q.trim()) {
      const s = q.toLowerCase();
      f = f.filter((e) => `${e.to} ${e.subject} ${e.body}`.toLowerCase().includes(s));
    }
    return f;
  }, [items, tab, q]);

  return (
    <ConsoleShell>
      <div className="max-w-5xl mx-auto px-5 lg:px-8 py-6">
        <div className="flex items-start justify-between mb-6 gap-4 flex-wrap">
          <div>
            <h1 className="font-display text-2xl lg:text-3xl font-semibold tracking-tight">Emails</h1>
            <p className="text-sm text-zinc-500 mt-1">
              Brouillons et envois depuis <span className="font-mono text-zinc-300">gabryx@chantix.pro</span>
            </p>
          </div>
          <button
            onClick={() => { setEditing(null); setShowCompose(true); }}
            data-testid="new-email-btn"
            className="inline-flex items-center gap-2 px-3.5 py-2 text-sm bg-white text-black hover:bg-zinc-200 transition"
          >
            <Plus className="w-4 h-4" />
            Nouveau brouillon
          </button>
        </div>

        <div className="mb-5 border-b border-white/10 flex gap-1" data-testid="emails-tabs">
          <TabBtn active={tab === "draft"} onClick={() => setTab("draft")} testid="tab-drafts">
            <Inbox className="w-4 h-4" />
            <span>Brouillons</span>
            <span className="text-[10px] font-mono text-zinc-500 ml-1">
              {counts.draft + counts.failed}
            </span>
          </TabBtn>
          <TabBtn active={tab === "sent"} onClick={() => setTab("sent")} testid="tab-sent">
            <CheckCircle2 className="w-4 h-4" />
            <span>Envoyés</span>
            <span className="text-[10px] font-mono text-zinc-500 ml-1">{counts.sent}</span>
          </TabBtn>
        </div>

        <div className="relative mb-4">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-zinc-500" />
          <input
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Rechercher destinataire, objet, contenu…"
            data-testid="emails-search"
            className="w-full bg-black border border-white/10 pl-10 pr-3 py-2 text-sm focus:border-white/30 outline-none"
          />
        </div>

        {loading ? (
          <div className="py-12 text-center text-zinc-500">
            <Loader2 className="w-5 h-5 animate-spin mx-auto" />
          </div>
        ) : filtered.length === 0 ? (
          <Empty tab={tab} />
        ) : (
          <div className="space-y-2" data-testid="emails-list">
            {filtered.map((e) => (
              <EmailRow
                key={e.id}
                email={e}
                onChanged={refresh}
                onEdit={() => { setEditing(e); setShowCompose(true); }}
              />
            ))}
          </div>
        )}
      </div>

      {showCompose && (
        <ComposeModal
          initial={editing}
          onClose={() => { setShowCompose(false); setEditing(null); }}
          onSaved={() => { setShowCompose(false); setEditing(null); refresh(); }}
        />
      )}
    </ConsoleShell>
  );
}

function TabBtn({ active, onClick, children, testid }) {
  return (
    <button
      onClick={onClick}
      data-testid={testid}
      className={`flex items-center gap-2 px-4 py-2.5 text-sm transition border-b-2 -mb-px ${
        active ? "border-white text-white" : "border-transparent text-zinc-500 hover:text-zinc-300"
      }`}
    >
      {children}
    </button>
  );
}

function EmailRow({ email, onChanged, onEdit }) {
  const [sending, setSending] = useState(false);

  const send = async () => {
    if (!confirm(`Envoyer cet email à ${email.to} ?`)) return;
    setSending(true);
    try {
      await api.post(`/emails/${email.id}/send`);
      toast.success(`Email envoyé à ${email.to}`);
      onChanged();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Échec de l'envoi");
      onChanged();
    } finally {
      setSending(false);
    }
  };

  const del = async () => {
    if (!confirm("Supprimer ce brouillon ?")) return;
    await api.delete(`/emails/${email.id}`);
    toast.success("Brouillon supprimé");
    onChanged();
  };

  const isSent = email.status === "sent";
  const isFailed = email.status === "failed";

  return (
    <div
      className="border border-white/10 hover:border-white/20 transition"
      data-testid={`email-row-${email.id}`}
    >
      <div className="p-4 flex items-start gap-3">
        <div className={`w-9 h-9 grid place-items-center flex-shrink-0 ${
          isSent ? "bg-emerald-500/10 text-emerald-400"
            : isFailed ? "bg-red-500/10 text-red-400"
            : "bg-pink-500/10 text-pink-400"
        }`}>
          {isSent ? <CheckCircle2 className="w-4 h-4" />
            : isFailed ? <AlertCircle className="w-4 h-4" />
            : <Mail className="w-4 h-4" />}
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 text-xs text-zinc-500 mb-0.5">
            <span className="font-mono">→ {email.to}</span>
            {email.kind && (
              <span className={`px-1.5 py-0.5 text-[9px] uppercase tracking-wider ${
                email.kind === "internal"
                  ? "bg-blue-500/10 text-blue-400 border border-blue-500/20"
                  : "bg-amber-500/10 text-amber-400 border border-amber-500/20"
              }`}>
                {email.kind === "internal" ? "Interne" : "Prospect"}
              </span>
            )}
            {email.agent_code === "clara" && (
              <span className="px-1.5 py-0.5 text-[9px] uppercase tracking-wider bg-pink-500/10 text-pink-400 border border-pink-500/20">
                ✨ CLARA
              </span>
            )}
          </div>
          <div className="text-sm font-medium truncate">{email.subject}</div>
          <div className="text-xs text-zinc-500 mt-1 line-clamp-2">{email.body}</div>
          <div className="text-[10px] text-zinc-600 font-mono mt-2">
            {isSent
              ? `Envoyé le ${new Date(email.sent_at).toLocaleString("fr-FR")}`
              : isFailed
                ? `Échec — ${new Date(email.created_at).toLocaleString("fr-FR")}`
                : `Brouillon — ${new Date(email.created_at).toLocaleString("fr-FR")}`}
          </div>
        </div>
        {!isSent && (
          <div className="flex items-center gap-1 flex-shrink-0">
            <button
              onClick={onEdit}
              data-testid={`email-edit-${email.id}`}
              className="p-2 text-zinc-400 hover:text-white hover:bg-white/10 transition"
              title="Modifier"
            >
              <Edit2 className="w-3.5 h-3.5" />
            </button>
            <button
              onClick={send}
              disabled={sending}
              data-testid={`email-send-${email.id}`}
              className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs bg-emerald-500/10 border border-emerald-500/30 text-emerald-300 hover:bg-emerald-500/20 transition disabled:opacity-50"
              title="Envoyer maintenant"
            >
              {sending ? <Loader2 className="w-3 h-3 animate-spin" /> : <Send className="w-3 h-3" />}
              Envoyer
            </button>
            <button
              onClick={del}
              data-testid={`email-delete-${email.id}`}
              className="p-2 text-zinc-500 hover:text-red-400 transition"
              title="Supprimer"
            >
              <Trash2 className="w-3.5 h-3.5" />
            </button>
          </div>
        )}
      </div>
      {isFailed && email.send_result?.error && (
        <div className="px-4 pb-3 text-[11px] text-red-400 font-mono border-t border-red-500/10 pt-2">
          Erreur SMTP : {email.send_result.error}
        </div>
      )}
    </div>
  );
}

function Empty({ tab }) {
  return (
    <div className="text-center py-16 border border-dashed border-white/10">
      <Mail className="w-10 h-10 text-zinc-700 mx-auto mb-4" />
      <div className="font-medium mb-2">
        {tab === "draft" ? "Aucun brouillon" : "Aucun email envoyé"}
      </div>
      <p className="text-sm text-zinc-500 max-w-md mx-auto">
        {tab === "draft"
          ? "Démarre un brouillon, ou demande à CLARA depuis le chat : « Rédige un mail pour Jean Dupont… »"
          : "Les emails envoyés apparaîtront ici."}
      </p>
    </div>
  );
}

function ComposeModal({ initial, onClose, onSaved }) {
  const [to, setTo] = useState(initial?.to || "");
  const [subject, setSubject] = useState(initial?.subject || "");
  const [body, setBody] = useState(initial?.body || "");
  const [aiIntent, setAiIntent] = useState("");
  const [busy, setBusy] = useState(false);
  const [aiBusy, setAiBusy] = useState(false);

  const save = async () => {
    if (!to.trim()) { toast.error("Destinataire requis"); return; }
    if (!subject.trim() || !body.trim()) { toast.error("Objet et corps requis"); return; }
    setBusy(true);
    try {
      if (initial?.id) {
        await api.patch(`/emails/${initial.id}`, { to, subject, body });
        toast.success("Brouillon mis à jour");
      } else {
        await api.post("/emails", { to, subject, body });
        toast.success("Brouillon enregistré");
      }
      onSaved();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Erreur enregistrement");
    } finally {
      setBusy(false);
    }
  };

  const saveAndSend = async () => {
    if (!to.trim() || !subject.trim() || !body.trim()) {
      toast.error("Tous les champs sont requis");
      return;
    }
    setBusy(true);
    try {
      let id = initial?.id;
      if (!id) {
        const { data } = await api.post("/emails", { to, subject, body });
        id = data.id;
      } else {
        await api.patch(`/emails/${initial.id}`, { to, subject, body });
      }
      await api.post(`/emails/${id}/send`);
      toast.success(`Email envoyé à ${to}`);
      onSaved();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Échec de l'envoi");
    } finally {
      setBusy(false);
    }
  };

  const askClara = async () => {
    if (!to.trim()) { toast.error("Renseigne d'abord le destinataire"); return; }
    if (!aiIntent.trim()) { toast.error("Décris brièvement l'intention du mail"); return; }
    setAiBusy(true);
    try {
      const { data } = await api.post("/emails/clara-draft", {
        to,
        intent: aiIntent,
        tone: "professionnel et chaleureux",
      });
      setSubject(data.subject);
      setBody(data.body);
      toast.success("CLARA a rédigé le brouillon ✨");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "CLARA n'a pas pu rédiger");
    } finally {
      setAiBusy(false);
    }
  };

  return (
    <div
      className="fixed inset-0 z-50 bg-black/80 backdrop-blur-sm flex items-start justify-center p-4 overflow-y-auto"
      data-testid="email-compose-modal"
      onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}
    >
      <div className="bg-[#0a0a0a] border border-white/10 w-full max-w-2xl my-8">
        <div className="flex items-center justify-between px-5 py-3 border-b border-white/10">
          <div className="font-display font-semibold">
            {initial ? "Modifier le brouillon" : "Nouveau brouillon"}
          </div>
          <button onClick={onClose} className="text-zinc-500 hover:text-white p-1" data-testid="compose-close">
            <X className="w-4 h-4" />
          </button>
        </div>

        <div className="p-5 space-y-4">
          <div>
            <div className="text-[10px] uppercase tracking-[0.2em] text-zinc-500 mb-1.5">De</div>
            <div className="font-mono text-sm text-zinc-400 border border-white/5 bg-white/[0.02] px-3 py-2">
              gabryx@chantix.pro
            </div>
          </div>

          <div>
            <div className="text-[10px] uppercase tracking-[0.2em] text-zinc-500 mb-1.5">À *</div>
            <input
              value={to}
              onChange={(e) => setTo(e.target.value)}
              type="email"
              placeholder="destinataire@example.com"
              data-testid="compose-to"
              className="w-full bg-black border border-white/10 px-3 py-2 text-sm focus:border-white/30 outline-none"
            />
          </div>

          <div className="border border-pink-500/20 bg-pink-500/5 p-3">
            <div className="flex items-center gap-2 mb-2">
              <Sparkles className="w-3.5 h-3.5 text-pink-400" />
              <div className="text-[10px] uppercase tracking-[0.2em] text-pink-400">
                Rédiger avec CLARA
              </div>
            </div>
            <input
              value={aiIntent}
              onChange={(e) => setAiIntent(e.target.value)}
              placeholder="ex: relance après notre échange LinkedIn d'il y a 2 semaines"
              data-testid="compose-ai-intent"
              className="w-full bg-black border border-white/10 px-3 py-2 text-sm mb-2 focus:border-white/30 outline-none"
            />
            <button
              onClick={askClara}
              disabled={aiBusy || !aiIntent.trim()}
              data-testid="compose-ask-clara"
              className="text-xs inline-flex items-center gap-1.5 px-3 py-1.5 border border-pink-500/30 text-pink-300 hover:bg-pink-500/10 transition disabled:opacity-50"
            >
              {aiBusy ? <Loader2 className="w-3 h-3 animate-spin" /> : <Sparkles className="w-3 h-3" />}
              {aiBusy ? "CLARA rédige…" : "Demander à CLARA"}
            </button>
          </div>

          <div>
            <div className="text-[10px] uppercase tracking-[0.2em] text-zinc-500 mb-1.5">Objet *</div>
            <input
              value={subject}
              onChange={(e) => setSubject(e.target.value)}
              placeholder="Objet du mail"
              data-testid="compose-subject"
              className="w-full bg-black border border-white/10 px-3 py-2 text-sm focus:border-white/30 outline-none"
            />
          </div>

          <div>
            <div className="text-[10px] uppercase tracking-[0.2em] text-zinc-500 mb-1.5">Corps *</div>
            <textarea
              value={body}
              onChange={(e) => setBody(e.target.value)}
              rows={10}
              placeholder="Bonjour…"
              data-testid="compose-body"
              className="w-full bg-black border border-white/10 px-3 py-2 text-sm focus:border-white/30 outline-none resize-y leading-relaxed"
            />
            <div className="text-[10px] text-zinc-600 mt-1">
              La signature « — Joël Castellanos · GABRYX » est ajoutée automatiquement.
            </div>
          </div>

          <div className="flex justify-end gap-2 pt-2 flex-wrap">
            <button
              onClick={onClose}
              className="px-4 py-2 text-sm border border-white/10 hover:bg-white/5 transition"
              disabled={busy}
            >
              Annuler
            </button>
            <button
              onClick={save}
              disabled={busy}
              data-testid="compose-save-draft"
              className="px-4 py-2 text-sm border border-white/15 hover:bg-white/5 transition disabled:opacity-50"
            >
              Enregistrer brouillon
            </button>
            <button
              onClick={saveAndSend}
              disabled={busy}
              data-testid="compose-send"
              className="px-4 py-2 text-sm bg-white text-black hover:bg-zinc-200 transition disabled:opacity-50 inline-flex items-center gap-2"
            >
              {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Send className="w-3.5 h-3.5" />}
              Envoyer maintenant
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
